import api, { UserSubscription, ProductTier } from '@sparemin/api-sdk';
import { DEFAULT_POLLING_INTERVAL_MILLIS } from 'config';
import { useCallback } from 'react';
import { Query, useQuery, UseQueryOptions } from 'react-query';
import { useAuth } from 'state/auth';
import { subscriptionQueryKeys } from './queryKeys';
import { isProGrowSelector, planNameSelector } from './selectors';

type QueryKey = ReturnType<typeof subscriptionQueryKeys.mySubscription>;

export interface UsePricingOptions<TData = UserSubscription>
  extends UseQueryOptions<UserSubscription, unknown, TData, QueryKey> {
  pollingInterval?: number;
  waitForTier?: ProductTier;
}

function usePlanSubscription<TData = UserSubscription>(
  opts?: UsePricingOptions<TData>,
) {
  const {
    enabled = true,
    pollingInterval = DEFAULT_POLLING_INTERVAL_MILLIS,
    waitForTier,
  } = opts ?? {};
  const { userId } = useAuth();

  const refetchInterval = useCallback(
    (
      _: TData | undefined,
      query: Query<UserSubscription, unknown, UserSubscription, QueryKey>,
    ) => {
      const tier = query.state.data?.subscribedProduct.tier;

      return tier !== waitForTier ? pollingInterval : false;
    },
    [pollingInterval, waitForTier],
  );

  return useQuery(
    subscriptionQueryKeys.mySubscription(),
    useCallback(() => api.getMySubscription(), []),
    {
      ...opts,
      enabled: !!userId && enabled,
      refetchInterval: waitForTier !== undefined ? refetchInterval : undefined,
    },
  );
}

export const usePlanName = () =>
  usePlanSubscription({ select: planNameSelector });

export const useIsProGrow = () =>
  usePlanSubscription({ select: isProGrowSelector });

export const usePollForNewTier = (tier?: ProductTier) =>
  usePlanSubscription({
    enabled: !!tier,
    waitForTier: tier,
  });
